import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import {
  EGenericInputTriggerType,
  GenericInputStatusEnum,
} from '../../../models/irembo-generic-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import {
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  findFormControl,
  findFormControlInScope,
  handleErrorMapping,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import { AbstractControl } from '@angular/forms';
import { getParentRepeaterKeyAndItemIndex } from '../../../../utils/utils/formly.utils';
import { TransformToArrayConfig } from '../../../models/multi-select-data-fetch.model';
import { finalize } from 'rxjs';
import { CustomGenericDataFetchComponent } from '../custom-generic-data-fetch/custom-generic-data-fetch.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

export interface ParamMapping {
  key: string;
  value?: any;
  sourceField?: string;
  searchInRootForm?: boolean;
  defaultValue?: any;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-direct-data-fetch',
  templateUrl: './custom-direct-data-fetch.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CustomDirectDataFetchComponent
  extends CustomGenericDataFetchComponent
  implements OnDestroy, AfterViewInit
{
  private localRepeaterForm: any;
  private localEnvironment: IEnvironment;

  constructor(
    private httpClient: HttpClient,
    private cdr: ChangeDetectorRef,
    protected override formStateService: FormStateService,
    modalService: NgbModal,
    @Inject('environment') environment: IEnvironment
  ) {
    super(httpClient, cdr, formStateService, modalService, environment);
    this.localEnvironment = environment;
  }

  override ngAfterViewInit(): void {
    this.initializeLocalRepeaterContext();
    super.ngAfterViewInit();
  }

  protected initializeLocalRepeaterContext() {
    const repeaterKeys = getParentRepeaterKeyAndItemIndex(this.field);
    if (repeaterKeys) {
      this.localRepeaterForm = this.form;
    } else {
      this.localRepeaterForm = getPopulateReferenceForm(this.field, this.form);
    }
  }

  private findLocalControl(
    targetKey: string,
    searchInRootForm?: boolean
  ): AbstractControl | null {
    if (searchInRootForm === true) {
      return findFormControl(this.localRepeaterForm, targetKey);
    }
    const isInRepeater = !!getParentRepeaterKeyAndItemIndex(this.field);

    if (searchInRootForm === false && isInRepeater) {
      return findFormControlInScope(this.localRepeaterForm, targetKey);
    }
    return isInRepeater
      ? findFormControlInScope(this.localRepeaterForm, targetKey)
      : findFormControl(this.localRepeaterForm, targetKey);
  }

  /**
   * Resolves a value from Static Config, Another Control, or Default
   */
  private resolveValue(inputValue: any, mapping: ParamMapping): any {
    if (mapping.value !== undefined) {
      return mapping.value;
    }

    if (mapping.sourceField) {
      const control = this.findLocalControl(
        mapping.sourceField,
        mapping.searchInRootForm
      );
      return control?.value ?? mapping.defaultValue;
    }

    return inputValue;
  }

  /**
   * Replaces Path Variables in URL (e.g. /users/:id)
   */
  private resolveUrl(url: string, inputValue: any): string {
    const pathParams = this.field.props['pathParams'] as ParamMapping[];
    if (!pathParams || !pathParams.length) return url;

    let resolvedUrl = url;
    pathParams.forEach(param => {
      const val = this.resolveValue(inputValue, param);
      // Replace :key or {key}
      resolvedUrl = resolvedUrl.replace(`:${param.key}`, String(val));
      resolvedUrl = resolvedUrl.replace(`{${param.key}}`, String(val));
    });
    return resolvedUrl;
  }

  /**
   * Builds Query Params
   */
  private buildQueryParams(inputValue: any): HttpParams {
    const queryParamsMapping = this.field.props[
      'queryParams'
    ] as ParamMapping[];
    let params = new HttpParams();

    if (!queryParamsMapping) return params;

    queryParamsMapping.forEach(mapping => {
      const val = this.resolveValue(inputValue, mapping);
      if (val !== null && val !== undefined && val !== '') {
        params = params.set(mapping.key, String(val));
      }
    });
    return params;
  }

  /**
   * Builds Body for POST/PUT/PATCH
   */
  private buildDirectBody(inputValue: any): Record<string, unknown> {
    const { useMultiplePayloadKeys, bodyMappings, payloadKey } =
      this.field.props;

    if (!useMultiplePayloadKeys || !bodyMappings?.length) {
      return payloadKey ? { [payloadKey]: inputValue } : inputValue;
    }

    const payload: Record<string, unknown> = {};
    bodyMappings.forEach((mapping: ParamMapping) => {
      payload[mapping.key] = this.resolveValue(inputValue, mapping);
    });
    return payload;
  }

  override onDataFetch(inputValue: string) {
    // Check if this is a "Direct" request (has httpMethod)
    const { httpMethod } = this.field.props;

    if (!httpMethod) {
      // fallback to existing Parent logic for Gateway calls
      super.onDataFetch(inputValue);
      return;
    }
    // Prevent double fetching
    if (this.statusClass === GenericInputStatusEnum.FETCHING) return;

    let { url } = this.field.props;

    if (this.field.props['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.localEnvironment);
    }

    const finalUrl = this.resolveUrl(url, inputValue);

    const httpParams = this.buildQueryParams(inputValue);

    let body = null;
    if (['POST', 'PUT', 'PATCH'].includes(httpMethod.toUpperCase())) {
      body = this.buildDirectBody(inputValue);
    }

    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.formControl.setErrors(null);
    this.formControl.updateValueAndValidity();
    this.formStateService.clearGlobalError(this.field);

    this.subscriptions.add(
      this.httpClient
        .request(httpMethod, finalUrl, {
          body: body,
          params: httpParams,
        })
        .pipe(
          finalize(() => {
            if (
              this.field.props?.['triggerType'] ===
              EGenericInputTriggerType.TRIGGER_BUTTON
            ) {
              this.field.props['runAction'] = false;
            }
          })
        )
        .subscribe({
          next: (res: any) => {
            this.handleDirectSuccess(res);
          },
          error: error => {
            this.handleDirectError(error);
          },
        })
    );
  }

  private handleDirectSuccess(res: any) {
    this.statusClass = GenericInputStatusEnum.SUCCESS;

    // Support both wrapped "response" or raw data
    const data = res['data'] || res;
    const response = data['response'] !== undefined ? data['response'] : data;

    this.formStateService.saveFetchedDataKeyInFormState(this.field, response);

    if (this.fieldsToPopulate.length > 0 && this.field.formControl.valid) {
      const transformConfig = this.field.props?.[
        'transformToArray'
      ] as TransformToArrayConfig;
      if (transformConfig?.enabled) {
        this.handleTransformToArray(response, transformConfig);
      }
      this.field.options?.detectChanges?.(this.field);

      populateFormFields(
        response,
        this.field,
        this.localRepeaterForm,
        this.formStateService
      );
    } else {
      this.field.options?.detectChanges?.(this.field);
    }

    this.formControl.setErrors(null);
    this.field.formControl.updateValueAndValidity();
    this.cdr.detectChanges();

    this.formStateService.emitFetchSuccess({
      fetchKey: this.field.key as string,
      componentId: this.componentId,
      response: res,
    });
    this.emitFetchOutcome('success');
  }

  /**
   * Error Handler for Direct Requests
   */
  private handleDirectError(error: any) {
    const errorPayload = error?.error || error;
    const { errors, statusClass } = handleErrorMapping(
      errorPayload,
      this.field.props['errorMapping'],
      'invalidGenericInput',
      this.field,
      this.formControl,
      true
    );

    const errorKey = Object.keys(errors)[0];
    const errorMessage =
      errors[errorKey]?.message || this.field.validation?.messages?.[errorKey];

    if (this.field?.props?.['showGlobalError'] && errorMessage)
      this.formStateService.addGlobalError(this.field, errorMessage);

    if (this.fieldsToPopulate.length > 0) {
      populateFormFields(
        errorPayload,
        this.field,
        this.localRepeaterForm,
        this.formStateService
      );
    }
    this.statusClass = statusClass;
    this.formControl.setErrors(errors);
    this.cdr.detectChanges();
    this.emitFetchOutcome('failure', error);
  }
}
